// **The rounds-mode Beekeeper channel** (rounds-slice implementation-review **F3**, client half).
//
// F3's server half landed three routes — read the colony, prompt one bee type, watch the sandbox's
// generation statuses — and stopped at the Rust boundary. Nothing in the browser called them, so
// `RoundsController` injected no `colonyController` and the prompt box was inert: the "edit via the
// Beekeeper" half of the change's own core loop could not be used at all.
//
// This module is the missing half. It is the rounds analogue of what the live `Connection` does for
// free: a mutation-dispatch seam that carries a prompt to the server, and a **status watch** that
// pushes what comes back into the same store slice the live socket's `PromptStatus` messages feed —
// so `Colony.tsx` renders a generation in rounds mode through exactly the code it already uses in
// live mode, with no rounds-specific branch anywhere in the screen except the cooldown (below).
//
// Four behaviours, in the order the loop needs them:
//
//   1. **Dispatch.** `prompt` goes to `POST /rounds/{sandbox}/types/{typeName}/prompt`. `handEdit`
//      goes to the Code tab's own two routes (beekeeper-agent-chat, CONTRACT.md §6) — a per-type
//      slice via `POST .../types/{typeName}/source`, or, via the `WHOLE_DOCUMENT_HAND_EDIT_TYPE_NAME`
//      sentinel the degraded whole-document view dispatches, the whole document via
//      `POST .../document`. `rollback`/`createType`/`setBirthType` still answer `not-ready`,
//      unchanged from the inert default: the rounds surface exposes no route for those three at
//      all, so claiming otherwise would be a lie the screen then acted on. Those are a successor
//      change's.
//   2. **The status watch.** One long-poll at a time, resumed by sequence number, applied in order.
//      The server long-polls for 20 s, so this is one request per generation step in practice, not
//      one per second.
//   3. **Closing the loop.** An `accepted` status means the sandbox's head moved, so the colony is
//      re-read and the store's `colonyMeta` replaced — otherwise the accepted program would be live
//      on the server and invisible in the editor, which is the one thing a player prompts *to see*.
//   4. **The advisory cooldown**, wall-clock, from the server's own remainder. The whole argument
//      for that lives in `ui/colony/prompt-cooldown.ts`; the mechanics live here.

import type { ColonyMeta, PromptStatus, RejectReason } from '../../net/wire/messages';
import type { HandEditDispatch, PromptDispatch, PromptStatusPage } from '../../net/rounds-http';
import {
  UNKNOWN_DOCUMENT_BASE_VERSION,
  WHOLE_DOCUMENT_HAND_EDIT_TYPE_NAME,
  type ColonyController,
} from '../../ui/colony/controller';
import type { AdvisoryPromptCooldown } from '../../ui/colony/prompt-cooldown';
import { performanceClock, type Clock } from '../../canvas/frame-scheduler';
import type { Scheduler } from '../../net/wire/connection';
import { useClientStore } from '../../state/store';

/** The subset of the rounds HTTP client this channel needs — `RoundsHttpClient` satisfies it. */
export interface RoundsPromptTransport {
  sendPrompt(typeName: string, promptText: string): Promise<PromptDispatch>;
  pollPromptStatus(afterSeq: number): Promise<PromptStatusPage | null>;
  fetchColony(): Promise<ColonyMeta | null>;
  handEditType(typeName: string, source: string, baseVersion: number | null): Promise<HandEditDispatch>;
  handEditDocument(source: string, baseVersion: number | null): Promise<HandEditDispatch>;
}

/**
 * The store surface this channel writes. A structural seam rather than the store itself, so the
 * channel is unit-testable against a plain object — and so every write it can perform is visible in
 * one place (`bindColonyRejection`'s precedent: the wire's store-facing half is explicit).
 */
export interface RoundsPromptStoreSeam {
  applyPromptStatus(status: PromptStatus): void;
  applyColonyMeta(meta: ColonyMeta): void;
  applyMutationRejected(reason: RejectReason): void;
}

/** The real store, as that seam. */
export function clientStorePromptSeam(): RoundsPromptStoreSeam {
  return {
    applyPromptStatus: (status) => useClientStore.getState().applyPromptStatus(status),
    applyColonyMeta: (meta) => useClientStore.getState().applyColonyMeta(meta),
    applyMutationRejected: (reason) => useClientStore.getState().applyMutationRejected(reason),
  };
}

export interface RoundsPromptChannelDeps {
  transport: RoundsPromptTransport;
  store: RoundsPromptStoreSeam;
  /** Wall-clock source for the cooldown countdown (production: `performance.now`). */
  clock?: Clock;
  /** Timer seam for the countdown tick and the idle-poll gap (`client-wire`'s own `Scheduler`). */
  scheduler?: Scheduler;
}

export interface RoundsPromptChannel {
  /** The `/colony` route's mutation-dispatch seam. */
  controller: ColonyController;
  /** The prompt box's wall-clock advisory countdown. */
  cooldown: AdvisoryPromptCooldown;
  /**
   * Starts the status watch; the returned function stops that run.
   *
   * Calling it again **retires the previous run and starts a fresh one**, so a `StrictMode`
   * setup → cleanup → setup cycle leaves exactly one loop applying statuses rather than two.
   */
  start(): () => void;
}

/** How long to wait before re-polling after a page that did not advance the cursor. The server's
 * own 20 s long poll is the real pacing; this only stops a transport that answers instantly (a
 * stub, a proxy that does not hold the request) from becoming a hot loop. */
const IDLE_POLL_GAP_MS = 250;

/** How long to wait before re-polling after a page that could not be read at all. */
const RETRY_GAP_MS = 1_000;

/** How often the countdown re-derives its whole-second value from the clock. Four times a second,
 * so the displayed second is never more than a quarter-second stale. */
const COUNTDOWN_TICK_MS = 250;

export function createRoundsPromptChannel(deps: RoundsPromptChannelDeps): RoundsPromptChannel {
  const { transport, store } = deps;
  const clock = deps.clock ?? performanceClock;
  const scheduler = deps.scheduler ?? wallClockScheduler();

  // --- the advisory cooldown ---------------------------------------------------------
  //
  // `remainingSeconds` is a CACHED integer, never a live computation: `useSyncExternalStore` reads
  // the snapshot during render and compares readings, so a getter that recomputed from the clock on
  // every call would report a new value mid-render and re-render forever.
  let deadlineMs: number | null = null;
  let remaining = 0;
  let countdownTimer: number | null = null;
  const listeners = new Set<() => void>();

  const publish = (next: number): void => {
    if (next === remaining) {
      return;
    }
    remaining = next;
    for (const listener of listeners) {
      listener();
    }
  };

  const secondsLeft = (): number =>
    deadlineMs === null ? 0 : Math.max(0, Math.ceil((deadlineMs - clock.now()) / 1000));

  const stopCountdown = (): void => {
    if (countdownTimer !== null) {
      scheduler.clearTimer(countdownTimer);
      countdownTimer = null;
    }
  };

  const tickCountdown = (): void => {
    countdownTimer = null;
    const next = secondsLeft();
    publish(next);
    if (next > 0) {
      countdownTimer = scheduler.setTimer(tickCountdown, COUNTDOWN_TICK_MS);
    } else {
      deadlineMs = null;
    }
  };

  /** Adopts the server's reading of the remainder. The server is the authority on the cooldown, so
   * every reading replaces the local deadline rather than being merged with it — that is what stops
   * the countdown drifting away from the thing that actually refuses a prompt. */
  const adoptRemainder = (remainingMs: number): void => {
    if (remainingMs <= 0) {
      stopCountdown();
      deadlineMs = null;
      publish(0);
      return;
    }
    deadlineMs = clock.now() + remainingMs;
    publish(secondsLeft());
    if (countdownTimer === null) {
      countdownTimer = scheduler.setTimer(tickCountdown, COUNTDOWN_TICK_MS);
    }
  };

  const cooldown: AdvisoryPromptCooldown = {
    remainingSeconds: () => remaining,
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
  };

  // --- the status watch --------------------------------------------------------------

  // Identity, not a boolean. React's `StrictMode` double-invokes a mount effect (setup → cleanup →
  // setup), so `start()` legitimately runs twice on one mount — and a boolean flag would let the
  // FIRST loop, parked on an `await`, see the flag true again and resume alongside the second. Each
  // run therefore carries its own token and stops the moment it is no longer the live one, so
  // exactly one loop is ever applying statuses.
  let activeWatch: object | null = null;
  /** The log cursor. Advanced only by a page that was read; a page that could not be is re-asked
   * for from the same place, so no status is skipped and none is applied twice. */
  let cursor = 0;

  const wait = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      scheduler.setTimer(resolve, ms);
    });

  const watch = async (token: object): Promise<void> => {
    while (activeWatch === token) {
      const page = await transport.pollPromptStatus(cursor);
      if (activeWatch !== token) {
        return;
      }
      if (page === null) {
        await wait(RETRY_GAP_MS);
        continue;
      }
      // `advanced` is the loop's only pacing guarantee, and it is deliberately about the CURSOR
      // rather than about the events: a well-behaved surface long-polls, so an empty page has
      // already cost 20 s and can be re-asked immediately, while a page that carries statuses
      // always moves the cursor. A page that moves neither — a stub, a proxy replaying a cached
      // body, a surface that regressed — must not be re-asked in a tight loop, because that loop
      // is an unbroken microtask chain that starves the whole event loop.
      const advanced = page.seq > cursor;
      cursor = Math.max(cursor, page.seq);
      adoptRemainder(page.cooldownRemainingMs);
      let accepted = false;
      for (const event of page.events) {
        store.applyPromptStatus(event);
        accepted = accepted || event.status.kind === 'accepted';
      }
      if (accepted) {
        // The head moved: re-read the colony so the editor shows the program that is now live. A
        // failed read changes nothing — the previous roster is still the truth we last saw, and
        // replacing it with an empty one would look like a colony that lost its bee types.
        const colony = await transport.fetchColony();
        if (colony !== null && activeWatch === token) {
          store.applyColonyMeta(colony);
        }
      }
      if (!advanced) {
        await wait(IDLE_POLL_GAP_MS);
      }
    }
  };

  // --- dispatch ----------------------------------------------------------------------

  const controller: ColonyController = {
    dispatch: (mutation) => {
      if (mutation.type === 'handEdit') {
        const { typeName, programSource, baseVersion } = mutation.value;
        // `Colony.tsx` sends `UNKNOWN_DOCUMENT_BASE_VERSION` (never a real ordinal — those are
        // 1-based) when no document version has been observed yet; the HTTP layer's own "no known
        // base" is `null`, not a magic number, so the translation happens once, here.
        const resolvedBaseVersion = baseVersion === UNKNOWN_DOCUMENT_BASE_VERSION ? null : baseVersion;
        void (async () => {
          const outcome =
            typeName === WHOLE_DOCUMENT_HAND_EDIT_TYPE_NAME
              ? await transport.handEditDocument(programSource, resolvedBaseVersion)
              : await transport.handEditType(typeName, programSource, resolvedBaseVersion);
          if (outcome.kind === 'refused') {
            // Same terminal-rejection contract as a refused prompt above: `Colony.tsx`'s
            // `ScriptBody` has already returned to read-only optimistically on this `sent`, and
            // only a rejection (or the colony re-read below) can reconcile that.
            store.applyMutationRejected(outcome.reason);
            return;
          }
          // The document changed — re-read the colony exactly as an accepted Beekeeper generation
          // does below, so the editor (including the flowchart tab's per-type `canonicalText`)
          // shows the player's own edit rather than the pre-edit snapshot. A failed read changes
          // nothing, matching the same fallback the prompt-accept path already accepts.
          const colony = await transport.fetchColony();
          if (colony !== null) {
            store.applyColonyMeta({
              // The document version ordinal: prefer a fresh reading from the re-read, but a `GET
              // /colony` that does not (yet) echo one back must not regress a value THIS save's own
              // response just reported.
              ...colony,
              documentVersion: colony.documentVersion ?? outcome.result.version,
            });
          }
        })();
        return 'sent';
      }
      if (mutation.type !== 'prompt') {
        // Unchanged from the inert default: the rounds surface exposes no route for these three, so
        // `not-ready` is the truthful answer rather than a silent no-op.
        return 'not-ready';
      }
      const { typeName, promptText } = mutation.value;
      void (async () => {
        const outcome = await transport.sendPrompt(typeName, promptText);
        if (outcome.kind === 'refused') {
          // The screen has already entered its optimistic pending state on this `sent`, and a
          // refused prompt produces no `PromptStatus` at all — so the rejection is the only thing
          // that can release it. `Colony.tsx` treats an observed rejection as terminal for exactly
          // that reason.
          store.applyMutationRejected(outcome.reason);
        }
      })();
      return 'sent';
    },
  };

  return {
    controller,
    cooldown,
    start: () => {
      const token = {};
      activeWatch = token;
      void watch(token);
      return () => {
        if (activeWatch === token) {
          activeWatch = null;
        }
        stopCountdown();
      };
    },
  };
}

/** The production timer seam (`bootstrap.tsx`'s `wallClockScheduler`, kept local so this module
 * imports no React tree). */
function wallClockScheduler(): Scheduler {
  return {
    setTimer: (fn, ms) => setTimeout(fn, ms) as unknown as number,
    clearTimer: (id) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>),
  };
}
